import React, { useState } from 'react';

/**
 * CommentThread - Visar konversationstråd mellan kund och byggkonsult.
 *
 * Om viewMode='consultant' OCH onEditEntry-prop är satt visas en pencil-ikon
 * på varje BK-meddelande (consultant). Klick öppnar inline-editor.
 *
 * @param {Array} messages - Array av { by, text, at, author, draft, editedAt? }
 * @param {string} viewMode - 'consultant' eller 'customer'
 * @param {Function} onEditEntry - Callback (entryIndex, newText) som anropas vid spara
 */
const CommentThread = ({ messages, viewMode = 'customer', onEditEntry }) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [saving, setSaving] = useState(false);

  if (messages.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">Ingen kommentar</div>
    );
  }

  const startEdit = (index, currentText) => {
    setEditingIndex(index);
    setEditingText(currentText);
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditingText('');
  };

  const saveEdit = async (index) => {
    if (!editingText.trim() || !onEditEntry) {
      cancelEdit();
      return;
    }
    setSaving(true);
    try {
      await onEditEntry(index, editingText.trim());
      cancelEdit();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-1 space-y-3">
      {messages.map((message, index) => {
        const canEdit = viewMode === 'consultant' && message.by === 'consultant' && onEditEntry && !message.draft;
        const isEditingThis = editingIndex === index;

        return (
          <div key={index}>
            {/* Author and timestamp */}
            <div className="text-xs text-gray-500 flex items-center gap-2">
              <span className="font-medium text-gray-800">
                {message?.author?.name || (message.by === 'consultant' ? 'Byggkonsult' : 'Kund')}
              </span>
              {message.at && (
                <span>
                  {new Date(message.at).toLocaleString('sv-SE', {
                    dateStyle: 'short',
                    timeStyle: 'short'
                  })}
                </span>
              )}
              {message.editedAt && (
                <span className="italic text-gray-400" title={`Redigerad ${new Date(message.editedAt).toLocaleString('sv-SE')}`}>
                  (redigerad)
                </span>
              )}
              {/* Oskickat utkast.
                  Texten sparas medan man skriver, vilket gör att den ser
                  skickad ut. Utan den här märkningen blev kommentarer liggande
                  i månader utan att någon förstod att de aldrig gått iväg. */}
              {message.draft === true && (
                <span
                  title="Utkast — syns inte för mottagaren förrän du skickar"
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 font-medium"
                >
                  Ej skickat
                </span>
              )}
              {canEdit && !isEditingThis && (
                <button
                  type="button"
                  onClick={() => startEdit(index, message.text)}
                  className="ml-auto text-xs text-blue-600 hover:text-blue-800 hover:underline"
                  title="Redigera detta svar"
                >
                  ✏ Redigera
                </button>
              )}
            </div>

            {/* Message text or edit form */}
            {isEditingThis ? (
              <div className="mt-1">
                <textarea
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                  rows={3}
                  className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                  autoFocus
                />
                <div className="flex gap-2 mt-1.5">
                  <button
                    type="button"
                    onClick={() => saveEdit(index)}
                    disabled={saving || !editingText.trim()}
                    className="text-xs px-3 py-1 bg-leadme-pink text-white rounded hover:bg-leadme-pink-dark disabled:opacity-50"
                  >
                    {saving ? 'Sparar…' : 'Spara'}
                  </button>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    disabled={saving}
                    className="text-xs px-3 py-1 text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                  >
                    Avbryt
                  </button>
                </div>
              </div>
            ) : (
              <div className={`text-sm mt-1 whitespace-pre-line ${
                message.by === 'customer' ? 'text-green-700' : 'text-gray-800'
              }`}>
                {message.text}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CommentThread;
